import Node from 'ui/Node';
import mixin from 'privates/utils/mixin';
import ViewportMixin from './mixins/ViewportMixin';
import * as members from 'privates/privateMembers';
import {validate} from 'privates/validations/validation';

/**
 * @mixin Element
 * @summary Provides basic functionality for elements.
 * @mixes $w.Node
 * @mixes $w.ViewportMixin
 * @memberof $w
 */
class Element extends Node {


    // PROPERTIES:

    /**
     * @member rendered
     * @label property
     * @syntax
     * get rendered(): boolean
     * @summary Indicates if an element is currently displayed.
     * @description
     *  If `rendered` is `true`, the element is in the current DOM structure and
     *  can be used.
     *
     *  If `rendered` is `false` the element is not in the current DOM structure.
     *
     *  Some reasons the element might not be in the DOM inclue:
     *    + It is in a [slide]($w.Slide.html) which is not currently showing.
     *    + It is in a mode which is not currently active. For example, it is in
     *      the hover mode of a [hover box]($w.HoverBox.html).
     * @type {external:Boolean}
     * @default `false`
     * @memberof $w.Element
     * @see [collapsed]($w.HiddenMixin.html#collapsed)
     * @see [hidden]($w.HiddenMixin.html#hidden)
     * @see [isVisible]($w.HiddenMixin.html#isVisible)
     * @readonly
     */
    get rendered() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getComp(compId).isDisplayed;
    }

    // METHODS:

    /**
     * @function onMouseIn
     * @syntax
     * function onMouseIn(handler: MouseEventHandler): Element
     * callback MouseEventHandler(event: MouseEvent, $w: Function): void
     * @summary Adds an event handler that runs when the mouse pointer is moved
     *  onto the element.
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the mouse pointer is moved onto the
     *  element.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [Element-onMouseIn.es6=Get the mouse event info when the mouse enters an element]
     * @eventtype mouseenter
     * @memberof $w.Element
     * @eventhandler
     */
    onMouseIn(cb) {
        const compType = members._type.get(this).compType;
        if (!validate(compType, 'onMouseIn', {onMouseIn: cb})) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().MOUSE_IN, controllerId, cb);
        return this;
    }

    /**
     * @function onMouseOut
     * @syntax
     * function onMouseOut(handler: MouseEventHandler): Element
     * callback MouseEventHandler(event: MouseEvent, $w: Function): void
     * @summary Adds an event handler that runs when the mouse pointer is moved
     *  off of the element.
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the mouse pointer is moved off of
     *  the element.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [Element-onMouseOut.es6=Get the mouse event info when the mouse exits an element]
     * @eventtype mouseleave
     * @memberof $w.Element
     * @eventhandler
     */
    onMouseOut(cb) {
        const compType = members._type.get(this).compType;
        if (!validate(compType, 'onMouseOut', {onMouseOut: cb})) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().MOUSE_OUT, controllerId, cb);
        return this;
    }

    toJSON() {
        const {id, type, global, rendered} = this;
        return {id, type, global, rendered};
    }
}

mixin(Element.prototype, ViewportMixin.prototype);

export default Element;



// WEBPACK FOOTER //
// ./js/modules/ui/Element.es6